import React from 'react';

import { Input } from 'components/ui/input';
import { Question } from 'types';
import { cn } from 'lib/utils';
import { getOptionTestId, getTestId } from 'utils/testUtils';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Icon } from '../icons/Icon';
import { QuestionAnswerFeedback } from './QuestionAnswerFeedback';

interface Props {
  question: Question;
  selectedOption?: number;
  showAnswerFeedback?: boolean;
  onChange: (optionId: number) => void;
  customOtherValue?: string;
  onCustomOtherChange?: (value: string) => void;
}

export const MultipleChoiceQuestion: React.FC<Props> = ({
  question,
  selectedOption,
  showAnswerFeedback = true,
  onChange,
  customOtherValue = '',
  onCustomOtherChange = () => {},
}) => {
  const selectedOptionIndex =
    selectedOption == null
      ? undefined
      : (question.options ?? []).findIndex((option) => option.id === selectedOption) + 1;
  const isCorrect =
    selectedOptionIndex && Array.isArray(question.correct_option_indexes)
      ? question.correct_option_indexes.includes(selectedOptionIndex)
      : undefined;

  return (
    <>
      <RadioGroup
          value={selectedOption != null ? selectedOption.toString() : ""}
          onValueChange={(value) => onChange(Number(value))}
        >
          {(question.options ?? []).map((option) => {
            const isSelected = selectedOption === option.id;
            return (
            <div
              key={option.id}
              {...getTestId(`radio-option-container-${option.id}`)}
              data-selected={isSelected ? 'true' : undefined}
              className={cn(
                'w-full flex flex-col gap-2 p-4 bg-white rounded-lg relative cursor-pointer border transition-colors focus-within:ring-2 focus-within:ring-foundation-orange-500 focus-within:ring-offset-2',
                isSelected
                  ? 'border-foundation-orange-500 shadow-[0_0_0_1px_var(--foundation-orange-500)]'
                  : 'border-transparent hover:bg-gray-50',
              )}
              onClick={() => onChange(option.id)}
            >
              {/* Option row */}
              <div className="flex flex-row items-center gap-2.5">
                <RadioGroupItem
                  value={option.id.toString()}
                  id={option.id.toString()}
                  className="focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2"
                />
                <Label
                  {...getOptionTestId(option.id ?? 0)}
                  {...getTestId(`radio-option-${option.id}`)}
                  htmlFor={option.id.toString()}
                  className={cn(
                    'cursor-pointer type-body-m text-foundation-base-darkbase-dark-500',
                    isSelected && 'font-semibold',
                  )}
                >
                  {option.front_text}
                </Label>
              </div>

              {/* Other input, stacked below */}
              {option.is_other && selectedOption === option.id && (
                <div
                  className="flex flex-row items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon name="downRightArrow" size={20} className="" />
                  <Input
                    {...getTestId('other-option-input')}
                    placeholder="Type your own answer"
                    aria-label="Your own answer"
                    value={customOtherValue}
                    onChange={(event) => onCustomOtherChange(event.target.value)}
                    required
                    autoFocus
                    className='h-10 text-base'
                  />
                </div>
              )}
            </div>
            );
          })}
        </RadioGroup>
      {showAnswerFeedback && isCorrect != null ? (
        <QuestionAnswerFeedback isCorrect={isCorrect} explanation={question.explanation} />
      ) : null}
    </>
  );
};
